import { Signal } from "@engine";
import type { InputService, SwipeEvent } from "@engine";

export type MenuModeKey = "story" | "endless" | "daily";

export class InputMap {
  readonly onRotateLeft = new Signal<void>();
  readonly onRotateRight = new Signal<void>();
  readonly onStart = new Signal<void>();
  readonly onRestart = new Signal<void>();
  readonly onTogglePause = new Signal<void>();
  readonly onPickMode = new Signal<MenuModeKey>();

  private unsubs: Array<() => void> = [];
  private allowGameplayInput = false;

  constructor(input: InputService) {
    this.unsubs.push(input.keyDown.on(this.onKey));
    this.unsubs.push(input.swipe.on(this.onSwipe));
    this.unsubs.push(input.tap.on(() => this.onAnyConfirm()));
  }

  setGameplayActive(active: boolean): void {
    this.allowGameplayInput = active;
  }

  private onKey = (e: KeyboardEvent): void => {
    const k = e.key;
    // Pause is a system-level command, not gameplay — emit it whether or
    // not gameplay input is currently active so the player can both
    // pause AND resume via the keyboard. The MorphHexGame handler is
    // gated by state so this is a no-op on menu/loading/dead.
    if (k === "p" || k === "P" || k === "Escape") {
      this.onTogglePause.emit();
      // Fall through so menu/dead screens still treat Esc as "any key".
    }
    if (this.allowGameplayInput) {
      if (k === "ArrowLeft" || k === "a" || k === "A") {
        this.onRotateLeft.emit();
        return;
      }
      if (k === "ArrowRight" || k === "d" || k === "D") {
        this.onRotateRight.emit();
        return;
      }
      // Pause keys consumed above, but only emit them; we don't want to
      // also fire "any confirm" while playing because that would restart
      // on dead transition lag.
      if (k === "p" || k === "P" || k === "Escape") return;
    } else {
      // Outside gameplay: 1/2/3 pick a mode on the menu screen. The
      // game-side handler ignores these unless state === "menu".
      if (k === "1") {
        this.onPickMode.emit("story");
        return;
      }
      if (k === "2") {
        this.onPickMode.emit("endless");
        return;
      }
      if (k === "3") {
        this.onPickMode.emit("daily");
        return;
      }
    }
    this.onAnyConfirm();
  };

  private onSwipe = (s: SwipeEvent): void => {
    if (!this.allowGameplayInput) {
      this.onAnyConfirm();
      return;
    }
    const horizontal = Math.abs(s.dx) > Math.abs(s.dy);
    if (!horizontal) return;
    if (s.dx < 0) this.onRotateLeft.emit();
    else this.onRotateRight.emit();
  };

  private onAnyConfirm(): void {
    this.onStart.emit();
    this.onRestart.emit();
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    this.onRotateLeft.clear();
    this.onRotateRight.clear();
    this.onStart.clear();
    this.onRestart.clear();
    this.onTogglePause.clear();
    this.onPickMode.clear();
  }
}
